import { DataGrid, InputV2 } from '@cimplico/components';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import AccountClassSelect from '../../../../../components/account-class-select/AccountClassSelect';
import XeroReportTaxTypeSelect from './XeroReportTaxTypeSelect';

type EntityTaxRateRowProps = {
    id: string;
    index: number;
    canRemove: boolean;
    isEditing: boolean;
    connectedToXero: boolean;
    onDelete: (index: number) => void;
};

function EntityTaxRateRow({ id, index, canRemove, isEditing, connectedToXero, onDelete }: EntityTaxRateRowProps): JSX.Element {
    const { control, setValue } = useFormContext();

    const name = useWatch({ control, name: `taxRates.${index}.name` });
    const nameError = useWatch({ control, name: `taxRates.${index}.nameError` });
    const accountClass = useWatch({ control, name: `taxRates.${index}.accountClass` });
    const accountClassError = useWatch({ control, name: `taxRates.${index}.accountClassError` });
    const usedOnXero = useWatch({ control, name: `taxRates.${index}.usedOnXero` });
    const usedOnJournal = useWatch({ control, name: `taxRates.${index}.usedOnJournal` });
    const xeroReportTaxType = useWatch({ control, name: `taxRates.${index}.xeroReportTaxType` });
    const xeroReportTaxTypeError = useWatch({ control, name: `taxRates.${index}.xeroReportTaxTypeError` });
    const rate = useWatch({ control, name: `taxRates.${index}.rate` });

    // Validate if tax rate value has a maxium of 4 digits after . and the maximum value is up to 100.0000
    const expression = '^(?:100|100[.][0]{0,4}|[0]?|0[.][0]{0,4}|[1-9]?[0-9][.][0-9]{0,4}|[1-9]?[0-9])$';
    const re = new RegExp(expression);

    return (
        <DataGrid.Row id={id} preventDrag={!isEditing}>
            <div
                className={`${!isEditing ? '-ml-28' : ''} py-1 md:py-0 grid ${
                    connectedToXero
                        ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-[25%_30%_30%_10%]'
                        : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-[37.5%_37.5%_20%]'
                } gap-2 md:gap-4 w-full`}
            >
                <div className="grow-0">
                    <Controller
                        name={`taxRates.${index}.name`}
                        control={control}
                        render={({ field: { onChange } }): JSX.Element => {
                            return (
                                <InputV2
                                    type="text"
                                    placeholder="Name..."
                                    value={name}
                                    errorMessage={nameError}
                                    disabled={!isEditing || usedOnXero || usedOnJournal}
                                    onChange={(val): void => {
                                        onChange(val);
                                        setValue(`taxRates.${index}.nameError`, undefined);
                                    }}
                                />
                            );
                        }}
                    />
                </div>
                <Controller
                    name={`taxRates.${index}.accountClass`}
                    control={control}
                    render={({ field: { onChange } }): JSX.Element => (
                        <AccountClassSelect
                            onChange={(val): void => {
                                onChange(val);
                                setValue(`taxRates.${index}.accountClassError`, undefined);
                            }}
                            values={accountClass || []}
                            errorMessage={accountClassError}
                            isDisabled={!isEditing || usedOnXero || usedOnJournal}
                        />
                    )}
                />
                {connectedToXero && (
                    <Controller
                        name={`taxRates.${index}.xeroReportTaxType`}
                        control={control}
                        render={({ field: { onChange } }): JSX.Element => (
                            <XeroReportTaxTypeSelect
                                value={xeroReportTaxType}
                                isDisabled={!isEditing || usedOnXero || usedOnJournal}
                                errorMessage={xeroReportTaxTypeError}
                                onChange={(val): void => {
                                    onChange(val);
                                    setValue(`taxRates.${index}.xeroReportTaxTypeError`, undefined);
                                }}
                            />
                        )}
                    />
                )}
                <div className="grow-0">
                    <Controller
                        name={`taxRates.${index}.rate`}
                        control={control}
                        render={({ field: { onChange } }): JSX.Element => (
                            <InputV2
                                type="text"
                                trailingText="%"
                                placeholder="0"
                                validator={(val): boolean => re.test(val)}
                                value={rate === undefined ? '' : rate}
                                disabled={!isEditing || usedOnXero || usedOnJournal}
                                disabledHint={
                                    isEditing && usedOnXero
                                        ? 'Tax rate is set by integration: Xero'
                                        : isEditing && usedOnJournal
                                        ? 'Tax rate is used on journal'
                                        : undefined
                                }
                                onChange={(val): void => onChange(val === '' ? undefined : val)}
                            />
                        )}
                    />
                </div>
            </div>
            {isEditing && (
                <button disabled={!canRemove} onClick={(): void => onDelete(index)} className="cursor-pointer disabled:cursor-default">
                    <TrashIcon className="w-4 h-4 text-gray-500" />
                </button>
            )}
        </DataGrid.Row>
    );
}

export default EntityTaxRateRow;
